import React, { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * <InfoTooltip> — icona ⓘ accanto a una label. Compatibile mobile:
 *  - Desktop: hover del mouse (Radix)
 *  - Mobile: tap sull'icona (state controllato), tap fuori per chiudere.
 *
 * L'icona è cliccabile come <button> con area touch ≥24×24 px.
 */
export default function InfoTooltip({
  content,
  children,
  side = "top",
  align = "start",
  iconClassName = "w-4 h-4 text-[#8A6D2E] hover:text-[#AC5D45]",
  className = "",
  as: Tag = "span",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Chiudi al tap fuori (solo quando aperto via tap)
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  if (!content) return <Tag className={className}>{children}</Tag>;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <Tag ref={ref} className={`inline-flex items-center gap-1 ${className}`}>
          {children}
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Mostra informazioni"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen((v) => !v);
              }}
              onTouchEnd={(e) => {
                // Su touch: forza il toggle e blocca il click fantasma
                e.preventDefault();
                e.stopPropagation();
                setOpen((v) => !v);
              }}
              className="inline-flex items-center justify-center min-w-[24px] min-h-[24px] p-0.5 rounded-full hover:bg-[#F3EEE7] active:bg-[#E8E2D9] transition"
            >
              <Info className={iconClassName} />
            </button>
          </TooltipTrigger>
        </Tag>
        <TooltipContent
          side={side}
          align={align}
          className="max-w-[280px] text-xs leading-relaxed bg-[#1E3A5F] text-white border-0 shadow-lg z-[100]"
          onPointerDownOutside={() => setOpen(false)}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
